import { useMemo, useState } from 'react';
import { useAnalyticsSales } from '@/api';
import {
  Button,
  Heading,
  Paragraph,
  Sheet,
  SheetContent,
} from '@/components/atoms';
import { PlusIcon, ShoppingBagIcon } from 'lucide-react';
import { useNavigate } from 'react-router';
import { routes } from '@/lib/constants';
import { ProductCreationForm } from '@/pages/dashboard/products/components';
import { formatCurrency } from '@/lib/helpers';

interface PerformanceProps {
  startDate?: string;
  endDate?: string;
}

export const Performance = ({ startDate, endDate }: PerformanceProps) => {
  const { data } = useAnalyticsSales(startDate, endDate);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [mode] = useState<'update' | 'create'>('create');

  const { topMonth, topYear } = useMemo(() => {
    const chart = data?.chart ?? [];
    if (chart.length === 0) {
      return { topMonth: '—', topYear: new Date().getFullYear().toString() };
    }
    const best = chart.reduce((acc, cur) =>
      (cur.sales ?? 0) > (acc.sales ?? 0) ? cur : acc
    );

    const formatPeriod = (period: string) => {
      if (!period || !period.includes('-')) return period;
      const [year, month] = period.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1);
      return date.toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
      });
    };

    return {
      topMonth: formatPeriod(best.month),
      topYear: new Date().getFullYear().toString(),
    };
  }, [data?.chart]);

  const toggleOpen = () => setOpen((prev) => !prev);

  return (
    <div className="col-span-6 sm:max-md:col-span-1 grid grid-cols-2 gap-2.5">
      {/* Top month card */}
      <div className="bg-white rounded-md border border-accent col-span-1 sm:max-md:col-span-2 p-4 flex flex-col gap-3">
        <Heading size="sm">Top month</Heading>
        <Paragraph size="lg" className="font-semibold">
          {topMonth}
        </Paragraph>
      </div>

      {/* Top year card */}
      <div className="bg-white rounded-md border border-accent col-span-1 sm:max-md:col-span-2 p-4 flex flex-col gap-3">
        <Heading size="sm">Top year</Heading>
        <Paragraph size="lg" className="font-semibold">
          {topYear}
        </Paragraph>
        <Paragraph size="sm" className="text-muted-foreground">
          {formatCurrency(data?.totalSales ?? 0)} sold so far
        </Paragraph>
      </div>

      {/* Top buyer card */}
      {/* <div className="bg-white rounded-md border border-accent col-span-1 sm:max-md:col-span-3 p-4 flex flex-col gap-3">
        <Heading size="sm">Top buyer</Heading>
        <div className="grid items-center gap-2">
          <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white text-sm font-medium">
            BN
          </div>
          <div>
            <Paragraph size="sm" className="font-medium">
              Buyers name
            </Paragraph>
            <Paragraph size="xs" className="text-muted-foreground">
              Location
            </Paragraph>
          </div>
        </div>
      </div> */}

      {/* Stacked action cards - Add new products and View recent sells */}
      <div className="col-span-3 sm:max-md:col-span-3 flex flex-col gap-2.5">
        {/* Add new products card */}
        <div className="bg-white rounded-md border border-accent p-4 flex items-center justify-between gap-3">
          <Heading size="sm">Add new products</Heading>
          <Button size="icon" className="w-12 h-12" onClick={toggleOpen}>
            <PlusIcon className="w-6 h-6" />
          </Button>
        </div>

        {/* View Recent Sells button card */}
        <div className="bg-white rounded-md border border-accent p-4 flex items-center justify-between">
          <Button
            className="w-full h-12 flex items-center gap-2"
            onClick={() => navigate(routes.sales)}
          >
            <ShoppingBagIcon className="w-4 h-4" />
            View recent orders
          </Button>
        </div>
      </div>

      {/* Product Creation Sheet */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="max-sm:w-screen overflow-y-auto pt-10 pb-4 px-4">
          <Heading size="xl">Create Product</Heading>
          <ProductCreationForm mode={mode} toggleOpen={toggleOpen} />
        </SheetContent>
      </Sheet>
    </div>
  );
};
